// src/components/SystemCard.js
import React, { useEffect, useMemo, useState } from "react";
import { Card, CardContent, Button, Typography, Chip, Box } from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import { generateSsoUrl, checkSystemHealth } from "../services/systemService";
import { useAuth } from "../context/AuthContext";

const SystemCard = ({ system, onAccess }) => {
  const { token } = useAuth();
  const [status, setStatus] = useState("checking"); // 'checking' | 'online' | 'offline'
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const runCheck = async () => {
      if (!system?.id || !system?.url) {
        if (!cancelled) setStatus("offline");
        return;
      }

      setStatus("checking");
      const online = await checkSystemHealth(system.id);
      if (!cancelled) setStatus(online ? "online" : "offline");
    };

    runCheck();

    return () => {
      cancelled = true;
    };
  }, [system?.id, system?.url]);

  const handleAccess = async () => {
    setLoading(true);
    try {
      if (system.ssoEnabled) {
        // Generate SSO URL via Envoy backend
        const url = await generateSsoUrl(system.id, token);
        if (url) window.open(url, "_blank", "noopener,noreferrer");
      } else {
        // Direct/manual login
        const baseUrl = (system.url || "").replace(/\/$/, "");
        const loginPath = system.loginPath || "/login";
        const target = `${baseUrl}${loginPath}`;
        window.open(target, "_blank", "noopener,noreferrer");
      }

      if (onAccess) onAccess(system);
    } catch (error) {
      console.error("Error accessing system:", error);
    } finally {
      setLoading(false);
    }
  };

  const statusChipSx = useMemo(() => {
    if (status === "online") {
      return {
        bgcolor: "rgba(16,185,129,0.10)",
        color: "#065f46",
        border: "1px solid rgba(16,185,129,0.25)",
      };
    }
    if (status === "checking") {
      return {
        bgcolor: "rgba(245,158,11,0.10)",
        color: "#92400e",
        border: "1px solid rgba(245,158,11,0.25)",
      };
    }
    return {
      bgcolor: "rgba(239,68,68,0.10)",
      color: "#991b1b",
      border: "1px solid rgba(239,68,68,0.25)",
    };
  }, [status]);

  return (
    <Card
      sx={{
        height: "100%",
        minHeight: 210, 
        borderRadius: 3,
        border: "1px solid rgba(0,0,0,0.10)",
        boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
        overflow: "hidden",
        position: "relative",
        transition: "transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease",
        "&:hover": {
          borderColor: "rgba(0,0,0,0.18)",
          boxShadow: "0 10px 24px rgba(0,0,0,0.08)",
          transform: "translateY(-2px)",
        },
      }}
    >
      <CardContent sx={{ p: 2.5, display: "flex", flexDirection: "column", gap: 1.25 }}>
        {/* Title + chevron */}
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1.5 }}>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                lineHeight: 1.15,
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                wordBreak: "break-word",
              }}
            >
              {system.name}
            </Typography>

            {/* Optional small id (kept subtle, won’t overflow) */}
            {system.id && (
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  fontWeight: 500,
                  letterSpacing: 0.6,
                  textTransform: "uppercase",
                }}
              >
                {String(system.id).toUpperCase()}
              </Typography>
            )}
          </Box>

          <ChevronRightIcon sx={{ color: "rgba(0,0,0,0.25)", mt: 0.5, flexShrink: 0 }} />
        </Box>

        {/* Description */}
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            fontSize: 13.5,
            lineHeight: 1.45,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {system.description || "Connect to this system via Envoy."}
        </Typography>

        {/* Chips */}
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 0.25 }}>
          <Chip
            label={status}
            size="small"
            sx={{
              fontWeight: 500,
              textTransform: "lowercase",
              ...statusChipSx,
            }}
          />
          <Chip
            label={system.ssoEnabled ? "Single sign-on" : "Manual login"}
            size="small"
            variant="outlined"
            sx={{
              fontWeight: 500,
              borderColor: "rgba(0,0,0,0.14)",
            }}
          />
        </Box>

        {/* CTA */}
        <Button
          onClick={handleAccess}
          disabled={status !== "online" || loading}
          width = "40%"
          sx={{
            mt: "auto",
            borderRadius: 99,
            py: 1.05,
            fontWeight: 500,
            bgcolor: status === "online" ? "primary.main" : "rgba(0,0,0,0.12)",
            color: status === "online" ? "#fff" : "rgba(0,0,0,0.45)",
            "&:hover": {
              bgcolor: status === "online" ? "rgba(37,99,235,0.92)" : "rgba(0,0,0,0.12)",
            },
          }}
        >
          {loading ? "Connecting..." : "Access System"}
        </Button>
      </CardContent>
    </Card>
  );
};

export default SystemCard;